import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { useReduxState } from 'hooks';
import { useApiData } from 'lib/api';
import { EmptyIndicator, FlexContainer, User } from 'components/common';
import { Box, BoxRow } from 'components/common/box';
import { Form, Select } from 'components/common/form';
import { CoursesLanguageDropdown } from 'components/courses';
import { VideosVideo } from '.';

import type { GetVideoTimesRes } from 'shared-types/responses';

const VideosList = () => {
  const {
    pageState: { course },
    messages
  } = useReduxState();

  const router = useRouter();

  const [users, setUsers] = useState<GetVideoTimesRes['courseUsers']>([]);
  const [languageOptions, setLanguageOptions] = useState<string[]>([]);

  const params = [
    router.query.userId ? `userId=${router.query.userId}` : '',
    router.query.languageVersion
      ? `languageVersion=${router.query.languageVersion}`
      : // filter by default language if no languageVersion is set
        `languageVersion=${course.language}`
  ];

  const { data, loading } = useApiData<GetVideoTimesRes>({
    url:
      `/admin/courses/${course.id}/analytics/videos` +
      (params.length ? `?${params.join('&')}` : '')
  });

  // update users and language options
  useEffect(() => {
    setUsers(data?.courseUsers || []);

    if (data?.languageVersions && data.languageVersions.length > 0) {
      setLanguageOptions(data.languageVersions);
    } else {
      setLanguageOptions([
        course.language,
        ...Object.keys(course.languageVersions || {})
      ]);
    }
  }, [data]);

  const handleLanguageVersionFilter = (value: string) => {
    const query = { ...router.query };
    if (value === course.language) delete query.languageVersion;
    else query.languageVersion = value;
    delete query.userId;
    router.replace({ query }, undefined, { shallow: true });
  };

  const handleUserSelect = (value: { id: number }) => {
    const userId = value?.id;
    const query = JSON.parse(JSON.stringify(router.query));
    if (!userId && !query.userId) return;

    if (!userId) delete query.userId;
    else query.userId = userId;

    router.replace({ query }, undefined, { shallow: true });
  };

  return (
    <div className="video-analytics">
      <Box
        loading={loading}
        headerContent={
          <FlexContainer style={{ flexWrap: 'wrap', gap: 15 }}>
            {Object?.keys(course?.languageVersions)?.length > 0 &&
              languageOptions.length > 0 && (
                <CoursesLanguageDropdown
                  category="lighter"
                  currentLanguage={
                    (router.query.languageVersion as string) || course.language
                  }
                  languageOptions={languageOptions}
                  onChange={handleLanguageVersionFilter}
                  renderInPortal={true}
                  size="medium"
                />
              )}

            <Form
              initialValues={{
                userId: parseInt((router.query?.userId as string) || '0', 10)
              }}
            >
              <Select
                field="userId"
                id="user-id"
                isClearable={true}
                isSearchable={true}
                noOptionsMessage={messages.commonNoUsers}
                onChange={handleUserSelect}
                options={users.map((user) => ({
                  label: (
                    <User
                      subtitle={user.email ? undefined : user.username}
                      user={user}
                    />
                  ),
                  value: {
                    id: user.id,
                    toString: () =>
                      `${user.username} ${user.email} ${user.firstName} ${user.lastName}`
                  }
                }))}
                placeholder={messages.commonUser}
                style={{ minWidth: 250 }}
              />
            </Form>
          </FlexContainer>
        }
        headerContentWrap={true}
        padding={0}
        title={messages.analyticsVideoAnalytics}
      >
        {data?.videos?.length ? (
          data.videos.map((video) => (
            <BoxRow key={video.id} padding={25}>
              <VideosVideo video={video} />
            </BoxRow>
          ))
        ) : (
          <EmptyIndicator
            icon="play-circle"
            title={messages.analyticsVideoNoVideos}
          />
        )}
      </Box>
    </div>
  );
};

export default VideosList;
